import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useSearchParams} from '@github-ui/use-navigate'
import {Button, Heading} from '@primer/react'
import {useState} from 'react'

import type {LabelArchiveType} from './constants/values'
import {LabelCreate} from './LabelCreate'
import {LabelList} from './LabelList'
import {SearchBar} from './LabelSearchBar'
import type {RepositoryNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'
import {parseArchivedParam} from './utils/archived-param'

export type RepositoryLabelsInternalFragment = Pick<
  RepositoryNode,
  | 'id'
  | 'nameWithOwner'
  | 'labels'
  | 'isWritable'
  | 'viewerCanCreateLabels'
  | 'viewerCanDeleteLabels'
  | 'viewerCanUpdateLabels'
>

type LabelsHeaderProps = {
  sidebarLayoutEnabled: boolean
  isControlled: boolean
  viewerCanCreateLabels: boolean
  onCreateDialogOpen: () => void
}

function LabelsHeader({
  sidebarLayoutEnabled,
  isControlled,
  viewerCanCreateLabels,
  onCreateDialogOpen,
}: LabelsHeaderProps) {
  return (
    <>
      <div className={styles.header}>
        {!sidebarLayoutEnabled && (
          <div className={styles.titleGroup}>
            <SidebarPageLayout.SidebarTrigger />
            <Heading as="h1" className={styles.heading}>
              Labels
            </Heading>
          </div>
        )}
        {!isControlled && viewerCanCreateLabels && (
          <Button variant="primary" onClick={onCreateDialogOpen}>
            New label
          </Button>
        )}
      </div>
      <SearchBar />
    </>
  )
}

type RepositoryLabelsInternalProps = {
  repository: RepositoryLabelsInternalFragment
  queryKey?: RouteQueryKey
  isCreateDialogOpen?: boolean
  isArchivingEnabled?: boolean
  isLoading?: boolean
  onCreateDialogOpenChange?: (open: boolean) => void
}

export function RepositoryLabelsInternal({
  repository,
  queryKey,
  isCreateDialogOpen: controlledOpen,
  isArchivingEnabled,
  isLoading,
  onCreateDialogOpenChange,
}: RepositoryLabelsInternalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = onCreateDialogOpenChange !== undefined
  const isCreateDialogOpen = isControlled ? !!controlledOpen : uncontrolledOpen
  const setIsCreateDialogOpen = isControlled ? onCreateDialogOpenChange : setUncontrolledOpen

  const data = repository

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const [searchParams] = useSearchParams()
  const archiveType: LabelArchiveType | undefined = isArchivingEnabled
    ? parseArchivedParam(searchParams.get('archived'))
    : undefined

  return (
    <>
      <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
        <LabelsHeader
          sidebarLayoutEnabled={sidebarLayoutEnabled}
          isControlled={isControlled}
          viewerCanCreateLabels={data.viewerCanCreateLabels}
          onCreateDialogOpen={() => setIsCreateDialogOpen(true)}
        />
        <ErrorBoundary boundaryName="LabelsPage" fallback={<div>Oops we could not load the labels</div>}>
          <LabelList
            repository={data}
            onCreateLabel={() => setIsCreateDialogOpen(true)}
            queryKey={queryKey}
            archiveType={archiveType}
            isLoading={isLoading}
            isArchivingEnabled={isArchivingEnabled}
          />
        </ErrorBoundary>
      </div>
      <LabelCreate
        repository={data}
        isOpen={isCreateDialogOpen}
        setDialogOpen={setIsCreateDialogOpen}
        queryKey={queryKey}
      />
    </>
  )
}
